import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.wormser-maxicar.de/api';

// Note: Fallback URLs and URL index removed as they're not currently used
// Can be re-added if multi-region support is needed in the future

// Singleton pattern to prevent multiple simultaneous CSRF requests
let csrfTokenPromise = null;
let lastFetchTime = 0;
let consecutiveFailures = 0;
let circuitBreakerActive = false;
const CSRF_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const MAX_CONSECUTIVE_FAILURES = 3;
const FAILURE_COOLDOWN = 30 * 1000; // 30 seconds cooldown after failures

// Initialize CSRF token on app startup
export const initializeCSRF = async () => {
  if (!isProduction()) {
    console.log('🔧 Development mode - CSRF initialization skipped');
    return null;
  }
  
  console.log('🔐 Initializing CSRF token for production...');
  
  try {
    // Check if CSRF token already exists and is recent
    let existingToken = getCSRFToken();
    const now = Date.now();
    
    if (existingToken && (now - lastFetchTime) < CSRF_CACHE_DURATION) {
      console.log('✅ CSRF token already available and cached');
      return existingToken;
    }
    
    // Use singleton pattern to prevent multiple requests
    if (csrfTokenPromise) {
      console.log('🔄 CSRF token fetch already in progress, waiting...');
      return await csrfTokenPromise;
    }
    
    // Create new promise for CSRF token fetch
    csrfTokenPromise = fetchCSRFTokenInternal();
    
    try {
      const token = await csrfTokenPromise;
      lastFetchTime = Date.now();
      return token;
    } finally {
      csrfTokenPromise = null;
    }
  } catch (error) {
    console.warn('Failed to initialize CSRF token:', error);
    csrfTokenPromise = null;
    return null;
  }
};

// Internal CSRF token fetching with retries and fallback token extraction
const fetchCSRFTokenInternal = async () => {
  let retries = 3;
  let existingToken = null;
  
  while (retries > 0 && !existingToken) {
    try {
      console.log(`🔄 Fetching CSRF token (attempt ${4 - retries}/3)...`);
      
      // First, test basic connectivity
      const response = await axios.get(`${API_BASE_URL}/auth/csrf/`, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        timeout: 15000,
      });
      
      console.log('📡 CSRF endpoint response:', response.status, response.statusText);
      
      // Wait for cookie to be set
      await new Promise(resolve => setTimeout(resolve, 200));
      
      existingToken = getCSRFToken();
      
      // PRODUCTION FIX: If cookie not set, try to extract from response headers or response body
      if (!existingToken && isProduction()) {
        console.log('🔧 Production mode: Cookie not set, trying alternative methods...');
        
        // Try to get token from response headers
        const headerToken = response.headers['x-csrftoken'] || 
                           response.headers['X-CSRFToken'] || 
                           response.headers['X-Csrftoken'];
        if (headerToken) {
          console.log('✅ CSRF token found in response headers');
          existingToken = headerToken;
          // Store it manually since cookies aren't working
          sessionStorage.setItem('csrf_token', headerToken);
          break;
        }
        
        // Try to get token from response body
        if (response.data && response.data.csrfToken) {
          console.log('✅ CSRF token found in response body');
          existingToken = response.data.csrfToken;
          // Store it manually since cookies aren't working
          sessionStorage.setItem('csrf_token', response.data.csrfToken);
          break;
        }
        
        console.warn('⚠️ CSRF endpoint responded but no token available via any method');
      }
      
      if (existingToken) {
        console.log('✅ CSRF token successfully obtained');
        break;
      } else {
        console.warn('⚠️ CSRF endpoint responded but no token was set');
        
        // Import and run diagnostics on cookie issue
        if (process.env.NODE_ENV === 'development') {
          try {
            const { diagnoseCookieIssue } = await import('./debugAPI');
            await diagnoseCookieIssue();
          } catch (debugError) {
            console.warn('Could not run cookie diagnostics:', debugError.message);
          }
        }
        
        // In production, continue without CSRF for now (backend should handle gracefully)
        if (isProduction()) {
          console.log('🚀 Production mode: Continuing without CSRF token (backend may handle gracefully)');
          return null;
        }
        
        // For now, treat this as a failure to trigger circuit breaker
        throw new Error('CSRF cookie not set despite successful response');
      }
    } catch (error) {
      retries--;
      console.warn(`❌ CSRF token fetch attempt failed:`, {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      // Specific error handling
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        console.error('🚨 Network connectivity issue detected. Backend may be unavailable.');
        
        // In development, provide helpful debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Debugging info:', {
            apiBaseUrl: API_BASE_URL,
            currentLocation: window.location.href,
            timestamp: new Date().toISOString()
          });
        }
      }
      
      if (retries > 0) {
        const delay = (4 - retries) * 1000; // 1s, 2s delays
        console.log(`⏳ Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  if (!existingToken) {
    const errorMessage = 'CSRF token fetch failed after 3 attempts';
    console.error('❌', errorMessage);
    console.log('🔍 Debug info:', {
      cookies: document.cookie,
      hostname: window.location.hostname,
      origin: window.location.origin,
      apiUrl: API_BASE_URL
    });
    
    // Increment consecutive failures for circuit breaker
    consecutiveFailures++;
    lastFetchTime = Date.now();
    
    throw new Error(errorMessage);
  }
  
  return existingToken;
};

// Fetch CSRF token with singleton pattern and enhanced circuit breaker
export const fetchCSRFToken = async () => {
  const now = Date.now();
  
  // Circuit breaker: if too many consecutive failures, wait for cooldown
  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
    if (now - lastFetchTime < FAILURE_COOLDOWN) {
      if (!circuitBreakerActive) {
        circuitBreakerActive = true;
        console.warn(`⚠️ CSRF circuit breaker ACTIVATED. Stopping all requests for ${FAILURE_COOLDOWN / 1000}s`);
      }
      return null;
    } else {
      console.log('🔄 Resetting CSRF circuit breaker after cooldown');
      consecutiveFailures = 0;
      circuitBreakerActive = false;
    }
  }
  
  // If circuit breaker was active and is now reset, log it
  if (circuitBreakerActive && consecutiveFailures < MAX_CONSECUTIVE_FAILURES) {
    circuitBreakerActive = false;
    console.log('✅ CSRF circuit breaker DEACTIVATED - Ready for new requests');
  }
  
  // Check if token exists and is recent
  const existingToken = getCSRFToken();
  
  if (existingToken && (now - lastFetchTime) < CSRF_CACHE_DURATION) {
    console.log('✅ Using cached CSRF token');
    consecutiveFailures = 0; // Reset failure count on success
    return existingToken;
  }
  
  // Use singleton pattern to prevent multiple requests
  if (csrfTokenPromise) {
    console.log('🔄 CSRF token fetch already in progress, waiting...');
    try {
      return await csrfTokenPromise;
    } catch (error) {
      // If the ongoing request fails, allow retry
      csrfTokenPromise = null;
      throw error;
    }
  }
  
  console.log('📡 Fetching new CSRF token from backend...');
  
  try {
    csrfTokenPromise = fetchCSRFTokenInternal();
    const token = await csrfTokenPromise;
    lastFetchTime = Date.now();
    consecutiveFailures = 0; // Reset failure count on success
    circuitBreakerActive = false;
    return token;
  } catch (error) {
    consecutiveFailures++;
    lastFetchTime = Date.now();
    console.error(`❌ Failed to fetch CSRF token (failure ${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES}):`, error.message);
    
    if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
      circuitBreakerActive = true;
      console.warn('⚠️ CSRF circuit breaker ACTIVATED due to consecutive failures');
      console.log('📊 Circuit breaker will reset after 30 seconds cooldown');
    }
    
    return null;
  } finally {
    csrfTokenPromise = null;
  }
};

// Get CSRF token from cookies or fallback storage
export const getCSRFToken = () => {
  // First try to get from cookies (standard method)
  const cookieToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  
  if (cookieToken) {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [CSRF Debug] Retrieved CSRF token from cookies:', 
        `${cookieToken.substring(0, 8)}...${cookieToken.substring(cookieToken.length - 4)}`);
    }
    return cookieToken;
  }
  
  // Fallback: try sessionStorage (for cross-origin issues)
  if (isProduction() || process.env.NODE_ENV === 'development') {
    const sessionToken = sessionStorage.getItem('csrf_token');
    if (sessionToken) {
      console.log('🔧 Using CSRF token from sessionStorage (cross-origin fallback)');
      return sessionToken;
    }
  }
  
  return null;
};

// Debug function to check CSRF status
export const debugCSRFStatus = () => {
  console.log('🔍 CSRF Debug Information:');
  console.log('  Environment:', isProduction() ? 'Production' : 'Development');
  console.log('  Current URL:', window.location.href);
  console.log('  API Base URL:', API_BASE_URL);
  
  const cookieToken = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
    ?.split('=')[1];
  
  const sessionToken = sessionStorage.getItem('csrf_token');
  
  console.log('  CSRF Cookie Token:', cookieToken ? 'Present (' + cookieToken.substring(0, 8) + '...)' : 'Not found');
  console.log('  Session Storage Token:', sessionToken ? 'Present (' + sessionToken.substring(0, 8) + '...)' : 'Not found');
  console.log('  All Cookies:', document.cookie);
  
  // Check if cookies are enabled
  document.cookie = 'test=1';
  const cookiesEnabled = document.cookie.includes('test=1');
  document.cookie = 'test=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  console.log('  Cookies Enabled:', cookiesEnabled);
  
  return { cookieToken, sessionToken, cookiesEnabled };
};

// Check if we're in production
export const isProduction = () => {
  // Handle both browser and Node.js environments
  const isProd = process.env.NODE_ENV === 'production';
  const isLocalhost = typeof window !== 'undefined' && 
                     window && window.location && 
                     (window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1');
  
  return isProd || !isLocalhost;
};

// Export all functions
const csrfUtils = {
  initializeCSRF,
  fetchCSRFToken,
  getCSRFToken,
  debugCSRFStatus,
  isProduction
};

export default csrfUtils;